import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import FB from "../facebook.png";
import IG from "../instagram.png";
import logo from "../picshotLogo.jpg";
import { api } from "../api";
import { PaymentSummary } from "../types";
import { formatPrice } from "../utils/payment";

function getBookingIdFromPath() {
  const parts = window.location.pathname.split("/").filter(Boolean);
  return parts[0] === "payment" ? parts[1] || "" : "";
}

function qrImageSrc(value?: string) {
  if (!value) return "";
  return value.startsWith("data:") ? value : `data:image/png;base64,${value}`;
}

function paymentStatusLabel(status?: string) {
  if (status === "PAID") return "Төлөгдсөн";
  if (status === "PENDING") return "Хүлээгдэж байна";
  if (status === "EXPIRED") return "Хугацаа дууссан";
  if (status === "FAILED") return "Амжилтгүй";
  if (status === "MANUAL") return "Гараар баталгаажсан";
  return "Үүсээгүй";
}

export function PaymentPage() {
  const bookingId = useMemo(getBookingIdFromPath, []);
  const [payment, setPayment] = useState<PaymentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [checking, setChecking] = useState(false);
  const [autoCreateAttempted, setAutoCreateAttempted] = useState(false);
  const [now, setNow] = useState(() => dayjs());
  const [error, setError] = useState("");

  const loadPayment = useCallback(async () => {
    if (!bookingId) {
      setError("Booking ID олдсонгүй.");
      setLoading(false);
      return;
    }

    try {
      setError("");
      const data = await api.getPayment(bookingId);
      setPayment(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Payment мэдээлэл татахад алдаа гарлаа.");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  const createInvoice = useCallback(async () => {
    if (!bookingId) return;
    setCreating(true);
    try {
      setError("");
      const data = await api.createQPayInvoice(bookingId);
      setPayment(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "QPay invoice үүсгэхэд алдаа гарлаа.");
    } finally {
      setCreating(false);
    }
  }, [bookingId]);

  async function checkPayment() {
    if (!bookingId) return;
    setChecking(true);
    try {
      setError("");
      const data = await api.checkQPayPayment(bookingId);
      setPayment(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Төлбөр шалгахад алдаа гарлаа.");
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    loadPayment();
  }, [loadPayment]);

  useEffect(() => {
    if (!payment || payment.status === "PAID" || payment.status === "MANUAL" || payment.qpayInvoiceId || creating || autoCreateAttempted) return;
    setAutoCreateAttempted(true);
    createInvoice();
  }, [autoCreateAttempted, createInvoice, creating, payment]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(dayjs()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  if (loading) {
    return <main className="payment-page"><section className="payment-card">Уншиж байна...</section></main>;
  }

  const booking = payment?.booking;
  const startTime = booking ? dayjs(`${booking.date}T${booking.time}`).format("YYYY-MM-DD, HH:mm") : "";
  const isPaid = payment?.status === "PAID";
  const isManual = payment?.status === "MANUAL";
  const isExpired =
    payment?.status === "EXPIRED" ||
    Boolean(payment?.qpayExpiresAt && dayjs(payment.qpayExpiresAt).isBefore(now));
  const expiryLabel = payment?.qpayExpiresAt ? dayjs(payment.qpayExpiresAt).format("HH:mm") : "";
  const qr = qrImageSrc(payment?.qpayQrImage);

  return (
    <main className="payment-page">
      <section className="payment-card">
        <img src={logo} className="payment-logo" alt="PicShot" />

        {booking && (
          <div className="payment-summary">
            <span className={isPaid ? "success-dot" : "pending-dot"}>{isPaid ? "✓" : "₮"}</span>
            <div>
              <h1>{isPaid || isManual ? "Цаг баталгаажлаа" : "Цаг бүртгэгдлээ"}</h1>
              <p><b>Багц:</b> {booking.eventTitle}</p>
              <p><b>Нэр:</b> {booking.name}</p>
              <p><b>И-мэйл:</b> {booking.email}</p>
              <p><b>Утас:</b> {booking.phone}</p>
              <p><b>Цаг:</b> {startTime}</p>
              <p><b>Төлөв:</b> {paymentStatusLabel(payment?.status)}</p>
            </div>
          </div>
        )}

        <div className="payment-intro">
          <h2>Урьдчилгаа төлбөр</h2>
          {!isExpired && !isPaid && !isManual && (
            <p>
            Та доорх QPay QR эсвэл банкны аппын холбоосоор {formatPrice(payment?.depositAmount || 0)}₮ төлж цагаа баталгаажуулна уу.
            </p>
          )}
        </div>

        {error && <div className="booking-error">{error}</div>}
        {isExpired && !isPaid && (
          <div className="booking-error">
            Invoice хугацаа дууссан байна. Энэ цаг суллагдсан тул буцаж дахин цаг сонгоно уу.
          </div>
        )}
        {!isExpired && expiryLabel && !isPaid && (
          <p className="important">QPay invoice {expiryLabel} хүртэл хүчинтэй.</p>
        )}

        {isManual && (
          <p className="important">
            Энэ захиалгыг админ гараар баталгаажуулсан тул QPay invoice үүсэхгүй.
          </p>
        )}

        {!payment?.qpayInvoiceId && !isPaid && !isManual && !isExpired && (
          <button className="primary-action" type="button" onClick={createInvoice} disabled={creating}>
            {creating ? "Invoice үүсгэж байна..." : "QPay invoice үүсгэх"}
          </button>
        )}

        {qr && !isPaid && !isManual && !isExpired && (
          <div className="qpay-box">
            <img className="qpay-qr" src={qr} alt="QPay QR" />
          </div>
        )}

        {!isPaid && !isManual && !isExpired && payment?.qpayShortUrl && (
          <a className="primary-action payment-link" href={payment.qpayShortUrl} target="_blank" rel="noreferrer">
            QPay-р төлөх
          </a>
        )}

        {!isPaid && !isManual && !isExpired && !payment?.qpayShortUrl && payment?.qpayDeeplink && (
          <a className="primary-action payment-link" href={payment.qpayDeeplink}>
            Банкны апп-р төлөх
          </a>
        )}

        {!isPaid && !isManual && !isExpired && Boolean(payment?.qpayUrls?.length) && (
          <div className="qpay-bank-grid">
            {payment?.qpayUrls?.map((item) => (
              <a key={item.link} href={item.link} className="qpay-bank-link">
                {item.logo && <img src={item.logo} alt="" />}
                <span>{item.description || item.name || "Банк"}</span>
              </a>
            ))}
          </div>
        )}

        <div className="payment-actions">
          <button type="button" onClick={checkPayment} disabled={!payment?.qpayInvoiceId || checking || isPaid || isManual || isExpired}>
            {checking ? "Шалгаж байна..." : "Төлбөр шалгах"}
          </button>
          <a href="/">Буцах</a>
        </div>

        {(isPaid || isManual) && (
          <p className="important">
            Таны цаг баталгаажлаа. {startTime} цагтаа ирнэ үү.
          </p>
        )}

        {!isPaid && !isManual && !isExpired && (
          <p className="important">
            Төлбөр төлсний дараа “Төлбөр шалгах” товчийг дарна уу.
          </p>
        )}

        <div className="socials">
          <a href="https://www.facebook.com/PicShotMongolia"><img src={FB} alt="facebook" /></a>
          <a href="https://www.instagram.com/picshot.mn/"><img src={IG} alt="instagram" /></a>
        </div>
      </section>
    </main>
  );
}
